import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ResultModel } from '../models/response/base.model';
import { ApiService } from './api.service';
import { GlobalService } from './global.service';
import { RestaurantCartStorageService } from './restaurant-cart-storage.service';

interface RestaurantOrderStart {
  restaurantId: string;
  inclusiveMode: boolean;
  tableNumber?: string | number | null;
  roomNumber?: string;
  roomOrderToken?: string;
  appendToOrderId?: string;
}

@Injectable({ providedIn: 'root' })
export class RestaurantOrderService {
  constructor(
    private router: Router,
    private apiService: ApiService,
    private globalService: GlobalService,
    private restaurantCartStorage: RestaurantCartStorageService
  ) {}

  start(order: RestaurantOrderStart): void {
    this.restaurantCartStorage.clear(order.restaurantId, true);
    this.restaurantCartStorage.clearOrderContext(order.restaurantId, true);
    this.globalService.setBookingsData(null);
    this.restaurantCartStorage.saveOrderContext(
      order.restaurantId,
      {
        restaurantId: order.restaurantId,
        inclusiveMode: order.inclusiveMode,
        orderType: 'Dine-in',
        delivery: 'Restaurant',
        tableNumber: order.inclusiveMode ? null : Number(order.tableNumber),
        roomNumber: order.inclusiveMode ? String(order.roomNumber || '').trim() : '',
        roomOrderToken: order.inclusiveMode ? order.roomOrderToken || '' : '',
        appendToOrderId: order.appendToOrderId || '',
      },
      true
    );
    sessionStorage.removeItem('completedRestaurantPaymentBackNavigation');
    this.router.navigate(
      this.globalService.getPortalRestaurantOrderRoute(order.restaurantId)
    );
  }

  getRestaurants(params: any): Observable<ResultModel> {
    return this.apiService.get<ResultModel>('/hotel/restaurants', params);
  }

  getRestaurant(params: any): Observable<ResultModel> {
    return this.apiService.get<ResultModel>(
      this.restaurantRoute(params),
      this.withoutPathIds(params)
    );
  }

  getTableStatus(params: any): Observable<ResultModel> {
    return this.apiService.get<ResultModel>(
      this.restaurantRoute(params, 'table-status'),
      this.withoutPathIds(params)
    );
  }

  createRoomOrderContext(body: any): Observable<ResultModel> {
    return this.apiService.post<ResultModel>(
      this.restaurantRoute(body, 'room-order-contexts'),
      this.withoutPathIds(body),
      { preferPortalAuth: true }
    );
  }

  createOrder(body: any): Observable<ResultModel> {
    return this.apiService.post<ResultModel>(
      this.restaurantRoute(body, 'orders'),
      this.withoutPathIds(body),
      { preferPortalAuth: true }
    );
  }

  getOrder(params: any): Observable<ResultModel> {
    return this.apiService.get<ResultModel>(
      this.orderRoute(params),
      this.withoutPathIds(params)
    );
  }

  updateGuestName(body: any): Observable<ResultModel> {
    return this.apiService.patch<ResultModel>(
      this.orderRoute(body, 'guest'),
      this.withoutPathIds(body)
    );
  }

  addItems(body: any): Observable<ResultModel> {
    return this.apiService.post<ResultModel>(
      this.orderRoute(body, 'items'),
      this.withoutPathIds(body),
      { preferPortalAuth: true }
    );
  }

  updateTip(body: any): Observable<ResultModel> {
    return this.apiService.patch<ResultModel>(
      this.orderRoute(body, 'tip'),
      this.withoutPathIds(body)
    );
  }

  getBillSplit(params: any): Observable<ResultModel> {
    return this.apiService.get<ResultModel>(
      this.orderRoute(params, 'bill-split'),
      this.withoutPathIds(params)
    );
  }

  createBillSplit(body: any): Observable<ResultModel> {
    return this.apiService.post<ResultModel>(
      this.orderRoute(body, 'bill-split'),
      this.withoutPathIds(body),
      { preferPortalAuth: true }
    );
  }

  updateSplitInvoicePaymentStatus(body: any): Observable<ResultModel> {
    return this.apiService.put<ResultModel>(
      this.orderRoute(body, 'bill-split', 'invoices', body?.invoiceId, 'payment-status'),
      this.withoutPathIds(body, 'invoiceId')
    );
  }

  abandonBillSplit(body: any): Observable<ResultModel> {
    return this.apiService.post<ResultModel>(
      this.orderRoute(body, 'bill-split', 'abandon'),
      this.withoutPathIds(body),
      { preferPortalAuth: true }
    );
  }

  private restaurantRoute(params: any, ...segments: string[]): string {
    return ['/hotel/restaurants', params?.ResturantId, ...segments].join('/');
  }

  private orderRoute(params: any, ...segments: string[]): string {
    return this.restaurantRoute(params, 'orders', params?.requestId, ...segments);
  }

  private withoutPathIds(params: any, ...additionalIds: string[]): any {
    const body = { ...(params || {}) };
    delete body.ResturantId;
    delete body.requestId;
    additionalIds.forEach((id) => delete body[id]);
    return body;
  }
}
